"use client";

import "./css/font-awesome.min.css";
import "./css/style.css";
import backgroundGif2 from "./img/gifWhite.gif";
import logo from "./img/Medco_full_Logo.png";
import featureImage from "./img/DemoImage.jpg";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import BookDemoModal from "./BookDemoModal";
import Navbar from "./Navbar";
import Hero from "./Hero";
import VideoSection from "./VideoSection";
import VideoDemoModal from "./VideoDemoModal";

function Home() {
  const auth = getAuth();
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const [videoUrls, setVideoUrls] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef([]);
  const carouselRef = useRef(null);

  // YouTube Shorts Links
  useEffect(() => {
    const rawVideoLinks = [
      "https://youtube.com/shorts/4zbxnBS75OA",
      "https://youtube.com/shorts/HnWc_Y6KHDo",
      "https://youtube.com/shorts/GnIfFtcxCsw",
      "https://youtube.com/shorts/FKAbnTej1ZI",
      "https://youtube.com/shorts/iWVmuaz2jiM",
      "https://youtube.com/shorts/rqAPjtxPxuk",
      "https://youtube.com/shorts/BVxhpu1lcdA",
      "https://youtube.com/shorts/CLpQRQMq_1k",
    ];

    const formattedUrls = rawVideoLinks.map((link) => {
      const videoId = link.split("/").pop();
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    });

    setVideoUrls(formattedUrls);
    setLoadingVideos(false);
  }, []);

  useEffect(() => {
    videoRefs.current = videoRefs.current.slice(0, videoUrls.length);
  }, [videoUrls]);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? videoUrls.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === videoUrls.length - 1 ? 0 : prev + 1));
  };

  const getLeftIndex = () =>
    activeIndex === 0 ? videoUrls.length - 1 : activeIndex - 1;
  const getRightIndex = () =>
    activeIndex === videoUrls.length - 1 ? 0 : activeIndex + 1;

  const getPositionClass = (index) => {
    if (index === activeIndex) return "active";
    if (index === getLeftIndex()) return "left";
    if (index === getRightIndex()) return "right";
    return "hidden";
  };

  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const handleBookDemo = () => setIsModalOpen(true);
  const handleWatchVideo = () => setIsVideoModalOpen(true);
  const handleCall = () => {
    window.location.href = "tel:+2010016128869";
  };
  const handleEmail = () => {
    window.location.href = "mailto:hazemtgs@me.com";
  };

  return (
    <div className="landing-page">
      <Navbar onBookDemo={handleBookDemo} />
      <Hero onBookDemo={handleBookDemo} onWatchVideo={handleWatchVideo} />

      <BookDemoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <VideoDemoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
      />

      <VideoSection
        loadingVideos={loadingVideos}
        videoUrls={videoUrls}
        handlePrev={handlePrev}
        handleNext={handleNext}
        getPositionClass={getPositionClass}
        videoRefs={videoRefs}
      />

      <section className="value-props">
        <div className="container">
          <h2 className="section-title">Why Choose Med-CO?</h2>
          <div className="props-wrapper">
            <div className="props-grid">
              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-clock-o"></i>
                </div>
                <h3>Save Multiple Hours Weekly</h3>
                <p>
                  Digitalize scheduling, billing, and record-keeping to focus on
                  patient care
                </p>
              </div>
              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-line-chart"></i>
                </div>
                <h3>Increase Revenue</h3>
                <p>
                  Reduce no-shows and streamline billing to improve cash flow
                </p>
              </div>
              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-shield"></i>
                </div>
                <h3>Safety</h3>
                <p>High-grade security to protect patient data</p>
              </div>
              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-users"></i>
                </div>
                <h3>Patient Portal</h3>
                <p>Empower patients with 24/7 access to their health records</p>
              </div>
              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-file-text"></i>
                </div>
                <h3>Automated Documentation</h3>
                <p>Reduce paperwork with smart templates and reports</p>
              </div>
              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-bar-chart"></i>
                </div>
                <h3>Real-time Analytics</h3>
                <p>Track clinic performance with customizable dashboards</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="feature-highlights">
        <div className="container">
          <div className="feature-row">
            <div className="feature-text">
              <h2>Comprehensive Patient Management</h2>
              <ul>
                <li>
                  <i className="fa fa-check"></i> Integrated electronic health
                  records
                </li>
                <li>
                  <i className="fa fa-check"></i> Customizable patient data
                </li>
                <li>
                  <i className="fa fa-check"></i> Appointment reminders
                </li>
              </ul>
            </div>
            <div className="feature-image">
              <img
                src={featureImage || "/placeholder.svg"}
                alt="Med-CO Dashboard"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="container">
          <h2>Ready to modernize your practice?</h2>
          <p>Join multiple clinics using Med-CO to deliver better care</p>
          <div className="cta-buttons">
            <button className="secondary-btn large" onClick={handleCall}>
              <i className="fa fa-phone"></i> Get Started Today
            </button>
          </div>
        </div>
      </section>

      <footer className="main-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-about">
              <img
                src={logo || "/placeholder.svg"}
                alt="Med-CO"
                className="footer-logo"
              />
              <p>
                The complete clinic management platform, for modern healthcare
                providers.
              </p>
            </div>
            <div className="footer-contact">
              <h4>Contact Us</h4>
              <p>
                <i className="fa fa-envelope"></i>
                <a href="#" onClick={handleEmail} className="footer-link">
                  ceo@thedevhouse.io
                </a>
              </p>
              <p>
                <i className="fa fa-phone"></i>
                <a href="#" onClick={handleCall} className="footer-link">
                  +20 100 16128869
                </a>
              </p>
              <p>
                <i className="fa fa-lock" />
                <Link
                  to="/privacy-policy"
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  Privacy Policy
                </Link>
              </p>
              <div className="social-links">
                <a
                  href="https://www.facebook.com/people/MedCo/61575253312860/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa fa-facebook"></i>
                </a>
                <a
                  href="https://www.instagram.com/medcoplatform/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa fa-instagram"></i>
                </a>
                <a
                  href="https://wa.me/+201001612869"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa fa-phone"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>
              &copy; {new Date().getFullYear()} Powered by{" "}
              <a
                href="https://thedevhouse.io"
                target="_blank"
                rel="noopener noreferrer"
              >
                The DevHouse
              </a>
              . All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
